import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid';

import { batchCreated, batchDeleted } from './batchesSlice';
import { bundleCreated, bundleDeleted } from './bundlesSlice';
import { productionComponentInstancesLoaded } from './productionComponentInstancesSlice';

type BundlesToShow = 'All' | 'BundlesWithWarnings';

interface BatchesAndBundlesTabState {
  readonly activeProjectGuid: string | null;
  readonly activeProductionGroupGuid: string | null;
  readonly checkedAnalysisSetGuids: readonly string[];
  readonly autoSelectNextWhenBundling: boolean;
  readonly bundlesToShow: BundlesToShow;
  readonly activeList: 'batches' | 'bundles';
  readonly batchesExpanded: string[];
  readonly bundlesExpanded: string[];
  readonly batchBeingEditedGuid: string | null;
  readonly bundleBeingEditedGuid: string | null;
  readonly userColumnWidths: TableColumnWidthInfo[] | null;
}

const initialState: BatchesAndBundlesTabState = {
  activeProjectGuid: null,
  activeProductionGroupGuid: null,
  checkedAnalysisSetGuids: [],
  autoSelectNextWhenBundling: false,
  bundlesToShow: 'All',
  activeList: 'batches',
  batchesExpanded: [],
  bundlesExpanded: [],
  batchBeingEditedGuid: null,
  bundleBeingEditedGuid: null,
  userColumnWidths: null,
};

const batchesAndBundlesTabSlice = createSlice({
  name: 'batchesAndBundlesTab',
  initialState,
  reducers: {
    setActiveProjectGuid: (state, action: PayloadAction<string | null>) => {
      state.activeProjectGuid = action.payload;
      state.activeProductionGroupGuid = null;
      state.checkedAnalysisSetGuids = [];
    },
    setActiveProductionGroupGuid: (state, action: PayloadAction<string | null>) => {
      state.activeProductionGroupGuid = action.payload;
      state.checkedAnalysisSetGuids = [];
    },
    checkRows: (state, action: PayloadAction<readonly string[]>) => {
      for (const checkedRow of action.payload) {
        state.checkedAnalysisSetGuids.push(checkedRow);
      }
    },
    uncheckRows: (state, action: PayloadAction<readonly string[]>) => {
      state.checkedAnalysisSetGuids = state.checkedAnalysisSetGuids.filter(
        (checkedRow) => !action.payload.includes(checkedRow),
      );
    },
    setAutoSelectNextWhenBundling: (state, action: PayloadAction<boolean>) => {
      state.autoSelectNextWhenBundling = action.payload;
    },
    setBundlesToShow: (state, action: PayloadAction<BundlesToShow>) => {
      state.bundlesToShow = action.payload;
    },
    setActiveList: (state, action: PayloadAction<'batches' | 'bundles'>) => {
      state.activeList = action.payload;
      state.batchBeingEditedGuid = null;
      state.bundleBeingEditedGuid = null;
      state.checkedAnalysisSetGuids = [];
    },
    expandBatch: (state, action: PayloadAction<string>) => {
      if (!state.batchesExpanded.includes(action.payload)) state.batchesExpanded.push(action.payload);
    },
    collapseBatch: (state, action: PayloadAction<string>) => {
      state.batchesExpanded = state.batchesExpanded.filter((batchGuid) => batchGuid !== action.payload);
    },
    expandBundle: (state, action: PayloadAction<string>) => {
      if (!state.bundlesExpanded.includes(action.payload)) state.bundlesExpanded.push(action.payload);
    },
    collapseBundle: (state, action: PayloadAction<string>) => {
      state.bundlesExpanded = state.bundlesExpanded.filter((bundleGuid) => bundleGuid !== action.payload);
    },
    setBatchBeingEditedGuid: (state, action: PayloadAction<string | null>) => {
      state.batchBeingEditedGuid = action.payload;
      state.bundleBeingEditedGuid = null;
    },
    setBundleBeingEditedGuid: (state, action: PayloadAction<string | null>) => {
      state.bundleBeingEditedGuid = action.payload;
      state.batchBeingEditedGuid = null;
    },
    setUserColumnWidths: (state, action: PayloadAction<TableColumnWidthInfo[]>) => {
      state.userColumnWidths = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(productionComponentInstancesLoaded, (state, action) => {
      if (action.payload.some((instance) => instance.productionGroupGuid === state.activeProductionGroupGuid))
        state.checkedAnalysisSetGuids = [];
    });
    builder.addCase(batchCreated, (state, action) => {
      state.batchesExpanded = [...state.batchesExpanded, action.payload.guid];
      state.checkedAnalysisSetGuids = [];
    });
    builder.addCase(bundleCreated, (state, action) => {
      state.bundlesExpanded = [action.payload.guid];
      state.checkedAnalysisSetGuids = [];
    });
    builder.addCase(batchDeleted, (state) => {
      state.batchBeingEditedGuid = null;
    });
    builder.addCase(bundleDeleted, (state) => {
      state.bundleBeingEditedGuid = null;
    });
  },
});

export const {
  setActiveProjectGuid,
  setActiveProductionGroupGuid,
  checkRows,
  uncheckRows,
  setAutoSelectNextWhenBundling,
  setBundlesToShow,
  setActiveList,
  expandBatch,
  collapseBatch,
  expandBundle,
  collapseBundle,
  setBatchBeingEditedGuid,
  setBundleBeingEditedGuid,
  setUserColumnWidths,
} = batchesAndBundlesTabSlice.actions;
export const batchesAndBundlesTab = batchesAndBundlesTabSlice.reducer;
